import { faTrash } from '@fortawesome/pro-regular-svg-icons/faTrash';
import { MediaQuery } from '@maxpreps/shared/styles/breakpoint';
import { ReactNode } from 'react';
import styled from 'styled-components';
import { COLOR } from '../../styles/colors';
import StyledButton, {
  ButtonFill,
  ButtonTheme,
  IButtonProps
} from '../../ui/Button/StyledButton';
import Icon, { IconType } from '../../ui/Icon/Icon';

export const StyledFormButtonManager = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-direction: column;
  gap: 8px;
  ${MediaQuery.Tablet} {
    flex-direction: row;
  }

  > button {
    width: 100%;
    ${MediaQuery.Tablet} {
      width: unset;
      min-width: 154px;
    }
  }

  .delete {
    display: flex;
    align-items: center;
    margin-right: auto;
    text-transform: unset;
    font-weight: normal;
  }
`;

export interface IExtraButtonConfig extends IButtonProps {
  text: string;
}

interface IFormButtonManagerProps {
  isLoading?: boolean;
  isSaveDisabled?: boolean;
  isDeleteDisabled?: boolean;
  onDelete?: Function;
  onCancel?: Function;
  onSavePlus?: Function;
  onSave?: Function | boolean; // Sometimes "button type submit" is enough
  onSaveText?: string;
  extraBtns?: IExtraButtonConfig[];
}

const FormButtonManager: React.FC<IFormButtonManagerProps> = ({
  isLoading = false,
  isSaveDisabled = false,
  isDeleteDisabled = false,
  onDelete,
  onCancel,
  onSavePlus,
  onSave,
  onSaveText = 'Save',
  extraBtns
}) => {
  const buttons: ReactNode[] = [];
  if (onDelete) {
    buttons.push(
      <StyledButton
        key="delete"
        className="delete"
        disabled={isLoading || isDeleteDisabled}
        onClick={onDelete}
        $fillMode={ButtonFill.Link}
        $colorScheme={ButtonTheme.Link}
      >
        <Icon
          iconType={IconType.FontAwesome}
          icon={faTrash}
          size={12}
          color={COLOR.black}
          $marginRight="4px"
        />
        Delete
      </StyledButton>
    );
  }
  if (onCancel) {
    buttons.push(
      <StyledButton
        key="cancel"
        $colorScheme={ButtonTheme.Link}
        $fillMode={ButtonFill.Outline}
        disabled={isLoading}
        onClick={onCancel}
      >
        Cancel
      </StyledButton>
    );
  }
  if (extraBtns) {
    extraBtns.forEach(({ text, $colorScheme, $fillMode, disabled, ...rest }) =>
      buttons.push(
        <StyledButton
          key={text}
          $colorScheme={$colorScheme ?? ButtonTheme.Link}
          $fillMode={$fillMode ?? ButtonFill.Outline}
          disabled={isLoading || disabled}
          {...rest}
        >
          {text}
        </StyledButton>
      )
    );
  }
  if (onSavePlus) {
    buttons.push(
      <StyledButton
        key="save-plus"
        type="submit"
        $colorScheme={ButtonTheme.Link}
        $fillMode={ButtonFill.Outline}
        disabled={isLoading || isSaveDisabled}
        onClick={onSavePlus}
      >
        Save + Add Another
      </StyledButton>
    );
  }
  if (onSave) {
    buttons.push(
      <StyledButton
        key="save"
        type="submit"
        $colorScheme={ButtonTheme.Link}
        $fillMode={ButtonFill.Fill}
        disabled={isLoading || isSaveDisabled}
        onClick={typeof onSave === 'function' ? onSave : undefined}
      >
        {onSaveText}
      </StyledButton>
    );
  }

  return <StyledFormButtonManager>{buttons}</StyledFormButtonManager>;
};

export default FormButtonManager;
